import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SalvButton, SalvTextArea, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import {
  InternalApiAlertType,
  InternalApiDraftReferenceType,
  InternalApiNoteDraft,
  InternalApiScreeningAlertStatus,
} from '../../../api-internal';
import { useAutoSaveNoteDraft } from '../../../note-drafts/useAutoSaveNoteDraft';
import { AutoSaveIndicator } from './AutoSaveIndicator';
import { NOTE_DRAFTS_QUERY_KEY, useCreateNoteDraft, useUpdateNoteDraft } from '../../../note-drafts';
import { AlertStatusSelection } from '../AlertStatusSelection';
import { faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Has from '../../../auth/Has';
import { CAN_USE_ALERT_NOTE_TEMPLATE } from '../../../auth/permissions';
import AlertNoteTemplateDropdown from '../../note-templates/AlertNoteTemplateDropdown';
import { useQueryClient } from '@tanstack/react-query';

export interface NoteSubmitOptions {
  draftId?: number;
}

interface NoteEditorProps {
  alertType: InternalApiAlertType;
  referenceId: number;
  referenceType: InternalApiDraftReferenceType;
  existingDraft?: InternalApiNoteDraft | null;
  selectableStatuses: InternalApiScreeningAlertStatus[];
  initialStatus?: string;
  isDraftEnabled: boolean;
  onSubmit: (content: string, status?: string, options?: NoteSubmitOptions) => Promise<void>;
  onCancel: () => void;
  onDraftIdChange?: (draftId: number | null) => void;
}

export const NoteEditor = ({
  alertType,
  referenceId,
  referenceType,
  existingDraft,
  selectableStatuses,
  initialStatus,
  isDraftEnabled,
  onSubmit,
  onCancel,
  onDraftIdChange,
}: NoteEditorProps) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(initialStatus);

  useEffect(() => {
    setSelectedStatus(initialStatus);
  }, [initialStatus]);

  const createDraftMutation = useCreateNoteDraft();
  const updateDraftMutation = useUpdateNoteDraft();

  const { content, setContent, saveState, currentDraftId, save, cancel, discard, resume } = useAutoSaveNoteDraft({
    mutations: {
      create: (content) => createDraftMutation.mutateAsync({ referenceId, referenceType, content }),
      update: (id, content) => updateDraftMutation.mutateAsync({ id, request: { content } }),
    },
    existingDraft,
    enabled: isDraftEnabled,
  });

  // Focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Notify parent when draft ID changes (for hiding draft from list while editing AND managing sync state)
  useEffect(() => {
    onDraftIdChange?.(currentDraftId);
  }, [currentDraftId, onDraftIdChange]);

  const handleSubmit = useCallback(async () => {
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const finalDraftId = await discard();
      await onSubmit(content, selectedStatus, { draftId: finalDraftId ?? undefined });

      if (finalDraftId && isDraftEnabled) {
        await queryClient.invalidateQueries({
          queryKey: [NOTE_DRAFTS_QUERY_KEY, referenceId, referenceType],
        });
      }
    } catch {
      resume();
    } finally {
      setIsSubmitting(false);
    }
  }, [content, selectedStatus, isDraftEnabled, referenceId, referenceType, queryClient, onSubmit, discard, resume]);

  const handleCancel = useCallback(async () => {
    if (isDraftEnabled) {
      await cancel();
      await queryClient.invalidateQueries({
        queryKey: [NOTE_DRAFTS_QUERY_KEY, referenceId, referenceType],
      });
    }
    // Parent is responsible for stopping editing sync
    onCancel();
  }, [isDraftEnabled, cancel, queryClient, referenceId, referenceType, onCancel]);

  const handleTemplateSelect = useCallback(
    (template: { content: string }) => {
      setContent(template.content);
    },
    [setContent]
  );

  const handleStatusChange = useCallback((status: string) => {
    setSelectedStatus(status);
  }, []);

  const clearStatus = useCallback(() => {
    setSelectedStatus(undefined);
  }, []);

  const selectedStatusObject = selectableStatuses.find((s) => s.code === selectedStatus);

  return (
    <div className='divider p-a-16 new-note'>
      <SalvTextArea
        ref={inputRef}
        ariaLabel='Note content'
        placeholder='Enter your note here...'
        value={content}
        onChange={setContent}
        size={Size.EXTRA_SMALL}
        autoSizing={true}
      />
      <div className='p-t-16 note-controls'>
        <Has allPermissions={CAN_USE_ALERT_NOTE_TEMPLATE}>
          <SalvTooltipWrapper tooltip='Insert note template'>
            <AlertNoteTemplateDropdown
              alertType={alertType}
              targetElement={inputRef}
              onTemplateSelect={handleTemplateSelect}
              disabled={false}
            />
          </SalvTooltipWrapper>
        </Has>
        <SalvTooltipWrapper tooltip='Optional selection to add status change with this note'>
          <AlertStatusSelection
            label={selectedStatusObject?.textName ?? 'Add status'}
            icon={selectedStatus ? undefined : faPlus}
            size={Size.SMALL}
            showNotes={false}
            selectableStatuses={selectableStatuses}
            selectStatus={handleStatusChange}
            selectNote={() => null}
          />
        </SalvTooltipWrapper>
        {selectedStatus && (
          <FontAwesomeIcon
            aria-label='Remove status assignment'
            size='lg'
            className='m-l-8'
            icon={faXmark}
            onClick={clearStatus}
            style={{ cursor: 'pointer' }}
          />
        )}
        <div className='spacer' />

        {isDraftEnabled && (
          <AutoSaveIndicator
            status={saveState.status}
            lastSavedAt={saveState.lastSavedAt}
            errorMessage={saveState.errorMessage}
            onRetry={save}
          />
        )}

        <SalvButton
          className='m-l-8 m-r-8'
          label={isDraftEnabled ? 'Close' : 'Cancel'}
          variant='muted'
          size={Size.SMALL}
          onClick={handleCancel}
          disabled={isSubmitting}
        />
        <SalvButton
          label='Submit'
          size={Size.SMALL}
          disabled={!content.trim() || isSubmitting}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};
